import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@mui/material/styles';
import CustomModal from './CustomModal';
import { CustomInputLabel } from './CustomInputs.styles';
import DisplayMedia from '../../../../components/DisplayMedia';
import { CustomTheme } from '../../../../context/PermissionsContext/ThemeContext/themesTypes';
import RewardType from '../_types/RewardType';

interface Props {
  label: string;
  value: string;
  onSelect: (reward: RewardType) => void;
  items: RewardType[];
}

const Container = styled.div<{
  theme?: CustomTheme;
  isDark?: boolean;
}>`
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
  background-color: ${({ theme, isDark }) =>
    theme?.custom?.background1 || (isDark ? '#121212' : 'white')};
  padding: 14px 16px;
  border-radius: 8px;
  align-items: center;
  cursor: pointer;
  border: 1px solid
    ${({ theme, isDark }) =>
      theme?.custom?.stroke || (isDark ? '#404040' : '#e0e0e0')};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  box-shadow: ${({ isDark }) =>
    isDark ? '0 1px 3px rgba(0, 0, 0, 0.3)' : '0 1px 2px rgba(0, 0, 0, 0.05)'};

  &:hover {
    border-color: ${({ theme }) => theme?.custom?.primary || '#ff6a00'};
    box-shadow: ${({ isDark }) =>
      isDark ? '0 2px 8px rgba(0, 0, 0, 0.4)' : '0 2px 4px rgba(0, 0, 0, 0.1)'};
  }
`;

const Label = styled.div<{
  theme?: CustomTheme;
  isPlaceholder?: boolean;
  isDark?: boolean;
}>`
  font-family: Manrope;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 22px;
  color: ${({ theme, isPlaceholder, isDark }) => {
    if (isPlaceholder) {
      return theme?.custom?.textLight || (isDark ? '#B0B0B0' : '#757575');
    }
    return theme?.custom?.text || (isDark ? '#FFFFFF' : '#333');
  }};
  font-feature-settings: 'liga' off, 'clig' off;
`;

const ChevronIcon = styled.img<{ isDark?: boolean }>`
  width: 16px;
  height: 16px;
  filter: ${({ isDark }) => (isDark ? 'invert(1)' : 'none')};
  transition: filter 0.2s ease;
`;

const ModalContentWrapper = styled.div<{
  theme?: CustomTheme;
  isDark?: boolean;
}>`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background-color: ${({ theme, isDark }) =>
    theme?.custom?.background1 || (isDark ? '#121212' : '#ffffff')};
`;

const ModalHeader = styled.div<{
  theme?: CustomTheme;
  isDark?: boolean;
}>`
  padding-bottom: 16px;
  border-bottom: 1px solid
    ${({ theme, isDark }) =>
      theme?.custom?.stroke || (isDark ? '#404040' : '#f0f0f0')};
  margin-bottom: 16px;
  flex-shrink: 0;
`;

const SubtitleText = styled.div<{
  theme?: CustomTheme;
  isDark?: boolean;
}>`
  font-family: Manrope;
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme, isDark }) =>
    theme?.custom?.textLight || (isDark ? '#B0B0B0' : '#757575')};
  margin-top: 4px;
`;

const RewardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  width: 100%;
  padding: 4px 0;

  @media (max-width: 800px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const RewardCard = styled.div<{ theme?: CustomTheme; isDark?: boolean }>`
  background-color: ${({ theme, isDark }) =>
    theme?.custom?.background2 || (isDark ? '#1E1E1E' : 'white')};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: ${({ isDark }) =>
    isDark
      ? '0px 2px 8px rgba(0, 0, 0, 0.3)'
      : '0px 2px 6px rgba(0, 0, 0, 0.08)'};
  cursor: pointer;
  border: 1px solid
    ${({ theme, isDark }) =>
      theme?.custom?.stroke || (isDark ? '#404040' : '#f0f0f0')};
  transition: all 0.2s ease-in-out;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ isDark }) =>
      isDark
        ? '0px 8px 20px rgba(0, 0, 0, 0.4)'
        : '0px 8px 16px rgba(0, 0, 0, 0.12)'};
    border-color: ${({ theme }) => theme?.custom?.primary || '#ff6a00'};
  }

  &:active {
    transform: translateY(-1px);
  }
`;

const ImageContainer = styled.div<{ isDark?: boolean }>`
  height: 140px;
  position: relative;
  background-color: ${({ isDark }) => (isDark ? '#2D2D2D' : '#f8f9fa')};
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const RewardContent = styled.div<{ theme?: CustomTheme; isDark?: boolean }>`
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  background-color: ${({ theme, isDark }) =>
    theme?.custom?.background2 || (isDark ? '#1E1E1E' : 'white')};
  position: relative;
  z-index: 1;
`;

const RewardName = styled.div<{ theme?: CustomTheme; isDark?: boolean }>`
  font-family: Manrope;
  font-weight: 600;
  font-size: 13px;
  color: ${({ theme, isDark }) =>
    theme?.custom?.text || (isDark ? '#FFFFFF' : '#333')};
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.3;
  max-width: 100%;
`;

const NoRewardsText = styled.div<{ theme?: CustomTheme; isDark?: boolean }>`
  font-size: 20px;
  font-family: Space Grotesk;
  font-weight: 500;
  text-align: center;
  color: ${({ theme, isDark }) =>
    theme?.custom?.textLight || (isDark ? '#B0B0B0' : '#757575')};
  padding: 60px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const NoRewardsSubtext = styled.div<{ theme?: CustomTheme; isDark?: boolean }>`
  font-size: 14px;
  margin-top: 8px;
  font-weight: 400;
  color: ${({ theme, isDark }) =>
    theme?.custom?.textLight || (isDark ? '#888888' : '#999999')};
`;

const CustomSelectRewards: React.FC<Props> = ({
  items,
  label,
  onSelect,
  value,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const theme = useTheme();
  const isDark = theme.custom?.isDark || false;

  useEffect(() => {
    if (openModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [openModal]);

  const handleWheel = (e: React.WheelEvent) => {
    const modalContent = e.currentTarget.closest('[data-modal-content]');
    if (!modalContent) return;

    const { scrollTop, scrollHeight, clientHeight } =
      modalContent as HTMLElement;
    const isAtTop = scrollTop === 0;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight;

    if ((e.deltaY < 0 && isAtTop) || (e.deltaY > 0 && isAtBottom)) {
      e.preventDefault();
    }
  };

  const renderRewardCards = () => (
    <RewardsGrid>
      {items.map((item, index: number) => (
        <RewardCard
          key={index}
          theme={theme as any}
          isDark={isDark}
          onClick={() => {
            onSelect(item);
            setOpenModal(false);
          }}
        >
          <ImageContainer isDark={isDark}>
            <DisplayMedia
              imageName={item.imageName}
              imageType={item.imageType}
              width="100%"
              height="100%"
              borderRadius={0}
              objectFit="cover"
              noWrapper
              sx={{ width: '100%', height: '100%', objectPosition: 'center' }}
            />
          </ImageContainer>
          <RewardContent theme={theme as any} isDark={isDark}>
            <RewardName theme={theme as any} isDark={isDark}>
              {item.name || 'Unnamed Reward'}
            </RewardName>
          </RewardContent>
        </RewardCard>
      ))}
    </RewardsGrid>
  );

  return (
    <>
      <CustomModal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        title="Select a Reward"
        width={800}
      >
        <ModalContentWrapper
          theme={theme as any}
          isDark={isDark}
          data-modal-content
          onWheel={handleWheel}
        >
          <ModalHeader theme={theme as any} isDark={isDark}>
            <SubtitleText theme={theme as any} isDark={isDark}>
              Choose a reward from the list below
              {items.length > 6 ? '. Scroll down to see more options' : ''}.
            </SubtitleText>
          </ModalHeader>
          <div>
            {items.length > 0 ? (
              <>{renderRewardCards()}</>
            ) : (
              <NoRewardsText theme={theme as any} isDark={isDark}>
                <div>No rewards available</div>
                <NoRewardsSubtext theme={theme as any} isDark={isDark}>
                  Create rewards in the Performance section first
                </NoRewardsSubtext>
              </NoRewardsText>
            )}
          </div>
        </ModalContentWrapper>
      </CustomModal>
      <CustomInputLabel theme={theme as any} style={{ marginBottom: '8px' }}>
        {label}
      </CustomInputLabel>
      <Container
        theme={theme as any}
        isDark={isDark}
        onClick={() => setOpenModal(true)}
      >
        <Label theme={theme as any} isDark={isDark} isPlaceholder={!value}>
          {value || 'Select a reward'}
        </Label>
        <ChevronIcon src="/images/chevron-down.svg" isDark={isDark} />
      </Container>
    </>
  );
};

export default CustomSelectRewards;
